// Camera-shutter effect: a brief white flash overlay + a synthesised
// click. Both capture paths (canvas-direct and capturePage) treat this
// as a POST-capture celebration. The pixels are already in hand by
// the time the flash starts, so there's no timing to coordinate with
// the screenshot. That decision makes the flash bulletproof: it
// cannot leak into the capture, and it cannot be blocked by the
// overlay-hide logic in the page fallback path.
//
// No external sound asset needed; Web Audio synthesises the click on
// demand.

import { getEffectiveSfxVolume } from '../arena/soundSettings';

const FLASH_ID = 'valk-shutter-flash';
const FADE_IN_MS = 70;
const PEAK_HOLD_MS = 70;
const FADE_OUT_MS = 260;
const PEAK_OPACITY = 0.95;

function playShutterClick() {
  const sfx = getEffectiveSfxVolume();
  if (sfx <= 0) return;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    const now = ctx.currentTime;

    // Short noise burst band-passed around 3 kHz. Gives the
    // characteristic mechanical-shutter "snap" rather than a thud.
    const bufferSize = Math.floor(ctx.sampleRate * 0.08);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    const decay = ctx.sampleRate * 0.018;
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / decay);
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 3200;
    filter.Q.value = 1.4;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.35 * sfx, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    noise.start(now);
    noise.stop(now + 0.08);
    noise.onended = () => ctx.close().catch(() => {});
  } catch {
    // Audio is nice-to-have; never break the capture flow.
  }
}

// Fire-and-forget shutter effect. Plays the click, fades a
// full-viewport white layer in to near-opaque, holds briefly, fades
// out. Safe to call from any context. No return value, no timing
// contract.
export function playShutterCosmetic() {
  playShutterClick();

  const existing = document.getElementById(FLASH_ID);
  if (existing) existing.remove();

  const el = document.createElement('div');
  el.id = FLASH_ID;
  el.setAttribute('data-screenshot-ignore', 'true');
  el.style.cssText = `
    position: fixed;
    inset: 0;
    background: #ffffff;
    z-index: 2147483647;
    pointer-events: none;
    opacity: 0;
    transition: opacity ${FADE_IN_MS}ms linear;
  `;
  document.body.appendChild(el);

  // rAF before setting the peak opacity so the initial opacity: 0
  // actually paints before the transition starts.
  requestAnimationFrame(() => {
    el.style.opacity = String(PEAK_OPACITY);
  });

  setTimeout(() => {
    if (!el.isConnected) return;
    el.style.transition = `opacity ${FADE_OUT_MS}ms ease-out`;
    el.style.opacity = '0';
    setTimeout(() => el.remove(), FADE_OUT_MS + 40);
  }, FADE_IN_MS + PEAK_HOLD_MS);
}
